/* eslint-disable camelcase */
/* eslint-disable react/no-danger */
import React from "react";
import { v4 as uuidv4 } from "uuid";
import dynamic from "next/dynamic";
import { useAppState } from "hooks/AppContext";
import { getJobTrackingProps } from "utils/tracking";
import { JobApplyButton } from "./JobApplyButton";
import { JWPlayerVideoModule } from "./JWPlayerVideoModule";
import { PerksAndBenefits } from "./PerksAndBenefits";
import { JobClientProvidedLocation } from "./JobClientProvidedLocation";
import { JobAlert } from "./JobAlert";
import styles from "./JobIndividualBody.module.scss";

const DynamicPerksAndBenefits = dynamic<React.ComponentProps<typeof PerksAndBenefits>>(
    () => import("./PerksAndBenefits").then((x) => x.PerksAndBenefits),
    { ssr: false }
);

const DynamicJobAlert = dynamic<React.ComponentProps<typeof JobAlert>>(
    () => import("./JobAlert").then((x) => x.JobAlert),
    { ssr: false }
);

export const JobIndividualBody = (props: {
    setIsOpen: (x: boolean) => void;
}) => {
    const { job, company } = useAppState();
    const {
        has_jwp_company_playlist,
        name: companyName = ""
    } = company;
    const {
        ats_info,
        compiled_contents = [],
        state = "",
        id: jobId,
        locations = [],
        has_jwp_hiring_manager_playlist = false,
    } = job;
    const {
        directApplyService = "",
        id: atsId = ""
    } = ats_info || {};
    const { setIsOpen } = props;
    const [jobAlertsViewed, setJobAlertsViewed] = React.useState(false);

    const changeToJobAlertsViewed = () => {
        setJobAlertsViewed(true);
    };

    const trackApplyProps = {
        ...getJobTrackingProps(job, company),
        placement_position: "desktop_bottom"
    };

    return (
        <section className={styles.jobBodyContainer}>
            <article className={styles.jobBodyDescription}>
                {compiled_contents.map((x: string, index: number) => (
                    <React.Fragment key={uuidv4()}>
                        <div key={uuidv4()} dangerouslySetInnerHTML={{ __html: x }} />
                        {/* Always after the first block, even if there is only one. */}
                        {index === 0 && <DynamicJobAlert job={job} formViewed={jobAlertsViewed} changeToFormViewed={changeToJobAlertsViewed} />}
                    </React.Fragment>
                ))}
            </article>
            <div className={styles.applyContainer}>
                {state === "live"
                    ? (
                        <JobApplyButton
                            className={styles.applyButton}
                            jobId={jobId}
                            setIsOpen={setIsOpen}
                            isDirectApply={!!directApplyService}
                            trackingProps={trackApplyProps}
                            directApplyVendor={directApplyService}
                        />
                    )
                    : null}
            </div>
            <div className={styles.jobBodyFooter}>
                <JobClientProvidedLocation locations={locations} />
                Job ID: {atsId}
            </div>
            { has_jwp_hiring_manager_playlist ? (
                <JWPlayerVideoModule
                    title="Meet the Hiring Manager"
                    description="Hear directly from the hiring manager about what it is like to work here."
                    tagName={jobId}
                />
            ) : null }
            <DynamicPerksAndBenefits company={company} />
            { has_jwp_company_playlist ? (
                <JWPlayerVideoModule
                    title="Company Videos"
                    description={`Hear directly from employees about what it is like to work at ${companyName}.`}
                    tagName={`${companyName},final`}
                />
            ) : null }
        </section>
    );
};
JobIndividualBody.displayName = "JobIndividualBody";
